// ============================================================
// Protocol v2 - Pure phase logic.
// No side effects. No data layer access. Fully unit-testable.
// ============================================================

import { FrequencyPattern } from '../data/types';
import type {
  Protocol,
  ProtocolComponent,
  ProtocolPhase,
  DoseLog,
} from '../data/types';

// ────────────────────────────────────────────────────────────
// Phase resolution
// ────────────────────────────────────────────────────────────

/**
 * Find the active phase for a component at a given moment in time.
 *
 * Resolution order:
 *  1. If currentPhaseId is set and points to a valid phase, return it
 *  2. Otherwise, fall back to phase 1
 *
 * Note: This function NEVER auto-advances based on dose count or calendar.
 * Phase advancement happens only via the explicit confirm gate.
 */
export function getCurrentPhase(
  component: ProtocolComponent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _now: Date = new Date(),
): ProtocolPhase {
  if (component.phases.length === 0) {
    throw new Error(`Component ${component.id} has no phases - invalid state`);
  }
  if (component.currentPhaseId) {
    const phase = component.phases.find((p) => p.id === component.currentPhaseId);
    if (phase) return phase;
  }
  return component.phases[0];
}

/**
 * Get the next phase in sequence after the current one.
 * Returns null if the current phase is maintenance or last in the list.
 */
export function getNextPhase(
  component: ProtocolComponent,
  currentPhase: ProtocolPhase,
): ProtocolPhase | null {
  if (currentPhase.isMaintenance) return null;
  const currentIndex = component.phases.findIndex((p) => p.id === currentPhase.id);
  if (currentIndex === -1) return null;
  const next = component.phases[currentIndex + 1];
  return next ?? null;
}

export interface EffectiveSchedule {
  frequency: FrequencyPattern;
  selectedDays?: number[];
  timesOfDay: string[];
  mealTiming?: 'fasted' | 'after_meal' | 'anytime' | null;
}

/**
 * Resolve the effective schedule for a component+phase combination.
 * Phase fields override component fields. Undefined override = inherit.
 * This is the SINGLE entry point for "what's the schedule right now?"
 */
export function getEffectiveSchedule(
  component: ProtocolComponent,
  phase: ProtocolPhase,
): EffectiveSchedule {
  return {
    frequency: phase.frequencyOverride ?? component.frequency,
    selectedDays: phase.selectedDaysOverride ?? component.selectedDays,
    timesOfDay: phase.timesOfDayOverride ?? component.timesOfDay,
    mealTiming: phase.mealTimingOverride ?? component.mealTiming,
  };
}

/**
 * Compute how many doses are consumed per 7-day week under a schedule.
 *
 * Used by inventory exhaustion math so "low stock" warnings reflect how long
 * the vial will actually last, not just the raw dose count. A vial with 3
 * doses on a daily protocol is different from 3 doses on a weekly protocol.
 *
 * Returns 0 when the schedule cannot be interpreted (no timesOfDay, or
 * SPECIFIC_DAYS with no selected days). Callers fall back to dose-count
 * rules in that case.
 */
export function dosesPerWeekFromSchedule(schedule: EffectiveSchedule): number {
  const perDay = schedule.timesOfDay?.length ?? 0;
  if (perDay <= 0) return 0;

  switch (schedule.frequency) {
    case FrequencyPattern.DAILY:
      return perDay * 7;
    case FrequencyPattern.EOD:
      return perDay * 3.5;
    case FrequencyPattern.WEEKLY:
      return perDay * 1;
    case FrequencyPattern.SPECIFIC_DAYS: {
      const days = schedule.selectedDays?.length ?? 0;
      return perDay * days;
    }
    default:
      return 0;
  }
}

export interface PhaseProgress {
  phaseId: string;
  dosesCompleted: number;
  dosesRequired: number | null;
  daysElapsed: number;
  daysRequired: number | null;
  percentComplete: number;
  isReadyToAdvance: boolean;
  readyReason?: 'doses_complete' | 'duration_complete' | 'both_complete';
}

function unitToDays(unit: 'days' | 'weeks' | 'months'): number {
  switch (unit) {
    case 'days': return 1;
    case 'weeks': return 7;
    case 'months': return 30;
  }
}

/**
 * Calculate progress through the current phase based on dose logs and phase metadata.
 *
 * Phase advancement is "ready" when EITHER the dose count target is met OR the
 * duration target is met (whichever applies, depending on durationUnit).
 *
 * Skipped doses DO count toward dosesCompleted - the user has explicitly chosen
 * to move past the dose. If they want more time, they should use Hold instead.
 */
export function getPhaseProgress(
  component: ProtocolComponent,
  phase: ProtocolPhase,
  doseLogs: DoseLog[],
  now: Date = new Date(),
): PhaseProgress {
  if (phase.isMaintenance) {
    return {
      phaseId: phase.id,
      dosesCompleted: 0,
      dosesRequired: null,
      daysElapsed: 0,
      daysRequired: null,
      percentComplete: 100,
      isReadyToAdvance: false,
    };
  }

  // Filter logs to this component + this phase. Skipped doses count.
  const phaseLogs = doseLogs.filter(
    (l) => l.componentId === component.id && l.phaseId === phase.id,
  );
  const dosesCompleted = phaseLogs.length;

  // Find when this phase started - earliest log
  const phaseStartIso = phaseLogs.length > 0
    ? phaseLogs
        .map((l) => l.loggedTime)
        .sort((a, b) => a.localeCompare(b))[0]
    : null;

  const daysElapsed = phaseStartIso
    ? Math.floor((now.getTime() - new Date(phaseStartIso).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  let dosesRequired: number | null = null;
  let daysRequired: number | null = null;

  if (phase.durationUnit === 'doses') {
    dosesRequired = phase.durationValue ?? null;
  } else if (
    phase.durationValue !== null &&
    phase.durationUnit !== null
  ) {
    daysRequired = phase.durationValue * unitToDays(phase.durationUnit);
  }

  const dosePct = dosesRequired ? (dosesCompleted / dosesRequired) * 100 : 0;
  const dayPct = daysRequired ? (daysElapsed / daysRequired) * 100 : 0;
  const percentComplete = Math.min(100, Math.max(dosePct, dayPct));

  const doseTargetMet = dosesRequired !== null && dosesCompleted >= dosesRequired;
  const dayTargetMet = daysRequired !== null && daysElapsed >= daysRequired;

  let isReadyToAdvance = false;
  let readyReason: PhaseProgress['readyReason'];
  if (doseTargetMet && dayTargetMet) {
    isReadyToAdvance = true;
    readyReason = 'both_complete';
  } else if (doseTargetMet) {
    isReadyToAdvance = true;
    readyReason = 'doses_complete';
  } else if (dayTargetMet) {
    isReadyToAdvance = true;
    readyReason = 'duration_complete';
  }

  return {
    phaseId: phase.id,
    dosesCompleted,
    dosesRequired,
    daysElapsed,
    daysRequired,
    percentComplete,
    isReadyToAdvance,
    readyReason,
  };
}

/**
 * Get the active dose for a specific component, accounting for phases.
 * Used by the dashboard, reminder engine, and notification body.
 */
export function getCurrentDose(
  protocol: Protocol,
  componentId: string,
  now: Date = new Date(),
): { dose: number; unit: 'mcg'; phase: ProtocolPhase; component: ProtocolComponent } {
  const component = protocol.components.find((c) => c.id === componentId);
  if (!component) {
    throw new Error(`Component ${componentId} not found in protocol ${protocol.id}`);
  }
  const phase = getCurrentPhase(component, now);
  return {
    dose: phase.doseMcg,
    unit: 'mcg',
    phase,
    component,
  };
}

export type PhaseAdvanceAction =
  | { type: 'advance_to_next' }
  | { type: 'hold_for_extension'; extensionDays: number; reason?: string }
  | { type: 'pause_protocol'; reason?: string };

/**
 * Compute the new component state after a user confirms a phase advancement decision.
 * Pure transform - the caller persists the result via updateProtocol().
 */
export function applyPhaseAdvance(
  component: ProtocolComponent,
  currentPhase: ProtocolPhase,
  action: PhaseAdvanceAction,
): { component: ProtocolComponent; protocolStillActive: boolean } {
  switch (action.type) {
    case 'advance_to_next': {
      const next = getNextPhase(component, currentPhase);
      if (!next) {
        return { component, protocolStillActive: true };
      }
      return {
        component: { ...component, currentPhaseId: next.id },
        protocolStillActive: true,
      };
    }
    case 'hold_for_extension': {
      return {
        component: {
          ...component,
          lastHoldExtendedAt: new Date().toISOString(),
          holdExtensionDays: action.extensionDays,
        },
        protocolStillActive: true,
      };
    }
    case 'pause_protocol': {
      return { component, protocolStillActive: false };
    }
  }
}

/**
 * Return all components across active protocols that are ready to advance phases.
 * Excludes components whose hold extension is still active.
 */
export function getProtocolsReadyForPhaseGate(
  protocols: Protocol[],
  doseLogs: DoseLog[],
  now: Date = new Date(),
): Array<{ protocol: Protocol; component: ProtocolComponent; phase: ProtocolPhase }> {
  const ready: Array<{ protocol: Protocol; component: ProtocolComponent; phase: ProtocolPhase }> = [];
  for (const protocol of protocols) {
    if (!protocol.isActive) continue;
    for (const component of protocol.components) {
      // Defensive: a malformed protocol with zero phases would crash
      // getCurrentPhase and break the entire dashboard render. Skip it
      // silently - the dashboard should degrade gracefully, not crash.
      if (component.phases.length === 0) continue;
      try {
        const phase = getCurrentPhase(component, now);
        const componentLogs = doseLogs.filter((l) => l.componentId === component.id);
        const progress = getPhaseProgress(component, phase, componentLogs, now);
        const nextPhase = getNextPhase(component, phase);
        if (progress.isReadyToAdvance && nextPhase) {
          // Skip if hold extension is active
          if (component.lastHoldExtendedAt && component.holdExtensionDays) {
            const expiresAt = new Date(component.lastHoldExtendedAt).getTime() + component.holdExtensionDays * 86_400_000;
            if (now.getTime() < expiresAt) continue;
          }
          ready.push({ protocol, component, phase });
        }
      } catch (err) {
        // Log and skip - a single malformed protocol should not take down
        // the entire Phase Ready section.
        console.error('[PRTCL] getProtocolsReadyForPhaseGate: skipping malformed component', component.id, err);
        continue;
      }
    }
  }
  return ready;
}
